import {Badge, Col, Row, Table} from "react-bootstrap";
import React, {useEffect, useState} from "react";
import {useParams} from "react-router-dom";
import {RequireAuth} from "Components/Security/RequireAuth";
import {DefaultContainer} from "Views/Shared/DefaultContainer";
import {useQuery} from "react-query";
import {useAppDispatch} from "hooks";
import {appLoading} from "Store/loadingSlice";
import {getInscriptionPayments} from "Api/Query/InscriptionPaymentQuery";
import {Notificator} from "Services/Notificator/Notificator";
import Pagination from "rc-pagination";
import {PaymentStatus} from "Model/Payment/PaymentStatus";
import {PAYMENT_STATUS_COLORS} from "Model/Payment/PaymentStatusColors";
import {PAYMENT_STATUS_LABELS} from "Model/Payment/PaymentStatusLabels";
import {PAYMENT_TYPE_NAMES} from "Model/Payment/PaymentTypes";

const PAGE_SIZE = 12

interface Props {
    inscriptionId?: string
}

export const AdminInscriptionPaymentList: React.FC<Props> = ({inscriptionId: inscriptionIdProp}) => {

    const {id: idFromParams} = useParams<{id: string}>()
    const id = inscriptionIdProp ?? idFromParams
    const dispatch = useAppDispatch()
    const isEmbedded = !!inscriptionIdProp
    const [page, setPage] = useState<number>(1)
    const [totalRecords, setTotalRecords] = useState<number>(0)
    const [payments, setPayments] = useState<any[]>([])
    const [filters, setFilters] = useState<{[key: string]: any}>({size: PAGE_SIZE, page: 1})

    const paymentsQuery = useQuery({
        enabled: false,
        queryKey: ['inscriptionPaymentsList', id],
        queryFn: () => getInscriptionPayments(id ?? '', filters),
        onSuccess: (response: any) => {
            const data = response?.data?._result ?? {}
            const items = data?.items ?? data ?? []
            setPayments(Array.isArray(items) ? items : [])
            setTotalRecords(data?.total ?? 0)
        },
        onError: () => {
            Notificator.error('Se ha producido un error al obtener los pagos.')
        }
    })

    const onPaginationPageChange = (selectedPage: number) => {
        setPage(selectedPage)
        setFilters({size: PAGE_SIZE, page: selectedPage})
    }

    useEffect(() => {
        if (id) {
            paymentsQuery.refetch()
        }
    }, [filters, id])

    useEffect(() => {
        dispatch(appLoading({value: paymentsQuery.isLoading || paymentsQuery.isRefetching, text: 'Cargando'}))
    }, [paymentsQuery.isLoading, paymentsQuery.isRefetching])

    const content = (
        <Col id={"inscriptionPaymentList"}>
            {!isEmbedded && (
                <Row className={"align-items-center mb-4"}>
                    <Col>
                        <h1>Pagos de la inscripción #{id}</h1>
                    </Col>
                </Row>
            )}

                    <Row>
                        <Col xs={12}>
                            {payments.length === 0 && !paymentsQuery.isLoading && !paymentsQuery.isRefetching ? (
                                <p>No se encontraron pagos para esta inscripción.</p>
                            ) : (
                                <>
                                    <Table striped bordered hover responsive>
                                        <thead>
                                        <tr>
                                            <th>#</th>
                                            <th>Importe</th>
                                            <th>Estado</th>
                                            <th>Método de pago</th>
                                            <th>Fecha</th>
                                        </tr>
                                        </thead>
                                        <tbody>
                                        {payments.map((payment: any, index: number) => {
                                            const rawStatus = (payment?.status?.name ?? payment?.status ?? '').toLowerCase() as PaymentStatus
                                            const statusLabel = PAYMENT_STATUS_LABELS[rawStatus] ?? rawStatus
                                            const statusColor = PAYMENT_STATUS_COLORS[rawStatus] ?? 'gray'
                                            const methodName = PAYMENT_TYPE_NAMES[payment?.paymentTypeId ?? payment?.paymentMethodId] ?? payment?.paymentMethodName ?? ''
                                            return (
                                                <tr key={payment?.id ?? index}>
                                                    <td>{payment?.id ?? ''}</td>
                                                    <td>{payment?.amount != null ? `${payment.amount} €` : ''}</td>
                                                    <td>
                                                        <Badge bg={statusColor}>
                                                            {statusLabel}
                                                        </Badge>
                                                    </td>
                                                    <td>{methodName}</td>
                                                    <td>{payment?.createdAt ?? ''}</td>
                                                </tr>
                                            )
                                        })}
                                        </tbody>
                                    </Table>
                                    {totalRecords > PAGE_SIZE &&
                                        <Pagination
                                            total={totalRecords}
                                            pageSize={PAGE_SIZE}
                                            current={page}
                                            onChange={onPaginationPageChange}
                                        />
                                    }
                                </>
                            )}
                        </Col>
                    </Row>
        </Col>
    )

    if (isEmbedded) {
        return content
    }

    return (
        <RequireAuth>
            <DefaultContainer>
                {content}
            </DefaultContainer>
        </RequireAuth>
    )
}
